import AgProfileSectionTable, {
  ProfileSectionTableData
} from '../../../stories/Components/AgSimpleTable';
import { Divider } from '@mui/material';
import AgButton from '../../../stories/Components/AgButton';
import AgTypography from '../../../stories/Styles/AgTypography';
import { useIntl } from 'react-intl';
import {
  ApiCountry,
  ApiIdentityOut
} from '@agunity/api-v4/build/data-contracts';
import AgAvatar from '../../../stories/Components/AgAvatar';
import defaultAvatar from '../../../stories/Components/AgAvatar/defaultAvatar.svg';
import AgMultipleCustomField from '../../../stories/Components/Cards/AgMultipleCustomField';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useEffect, useState } from 'react';
import { CustomFieldGroupProps } from './EditStaffDetails';
import {
  CustomFieldColumnType,
  MANDATORY_LEVEL,
  MEMBER_FIELD_NAME_NOTES,
  MEMBER_PARENT_FIELD_TYPE_NUMBER
} from '../../../Constants';
import {
  setIsOpenViewAvatar,
  setStaff_CountryOptions
} from '../../../redux/slices/staff';
import { getCountries } from '../../../redux/slices/countries';

export interface ViewStaffDetailsProps {
  identity?: ApiIdentityOut;
  imageURL?: string;
}

const ViewStaffDetails = ({ identity, imageURL }: ViewStaffDetailsProps) => {
  const messages = useIntl().formatMessage;
  const dispatch = useAppDispatch();

  const latitude = identity?.address?.location?.Y || '';
  const longitude = identity?.address?.location?.X || '';
  const tagList = useAppSelector((state) => state.staff.tagList);
  const [customFieldGroupData, setCustomFieldGroupData] = useState<
    CustomFieldGroupProps[]
  >([]);
  const [countries, setCountries] = useState<ApiCountry[]>();
  const [selectedCountry, setSelectedCountry] = useState<ApiCountry>();

  useEffect(() => {
    const f = async () => {
      try {
        if (!countries || countries?.length === 0) {
          const cou = await dispatch(getCountries());
          if (cou?.data?.length) {
            const countryList = cou.data;
            setCountries(countryList);
            dispatch(setStaff_CountryOptions(cou.data));
          }
        }
        if (countries?.length && identity?.address?.countryId) {
          const countryObj = countries.find(
            (country) => country.id === identity?.address?.countryId
          );
          countryObj && setSelectedCountry(countryObj);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    };
    f();
  }, [countries, dispatch, identity?.address?.countryId]);

  useEffect(() => {
    if (!tagList?.length || !identity?.data?.length) return;
    let customFieldGroupData: CustomFieldGroupProps[] = [];
    identity.data.forEach((field) => {
      // as for custom field, the value comes from account.data, but the name of key come from custom field API
      const customField = tagList.find(
        (tag) => tag.key?.toUpperCase() === field.key.toUpperCase()
      );
      if (!customField) return;
      const parentField = tagList.find(
        (tag) => tag.id === customField.parentId
      );
      if (
        !parentField ||
        parentField.fieldType !== MEMBER_PARENT_FIELD_TYPE_NUMBER
      )
        return;
      const isParentFieldExist = customFieldGroupData.find(
        (field) => field.groupName === parentField.key
      );
      if (isParentFieldExist) {
        isParentFieldExist.data.push({
          name: customField.key ?? '',
          value: field.value ?? '',
          unitId: customField.unitId,
          mandatoryLevel:
            customField.mandatoryLevel ?? MANDATORY_LEVEL.Optional,
          fieldType: customField.fieldType ?? CustomFieldColumnType.None
        });
      } else {
        customFieldGroupData.push({
          groupName: parentField.key ?? '',
          data: [
            {
              name: customField.key ?? '',
              value: field.value ?? '',
              unitId: customField.unitId,
              mandatoryLevel:
                customField.mandatoryLevel ?? MANDATORY_LEVEL.Optional,
              fieldType: customField.fieldType ?? CustomFieldColumnType.None
            }
          ]
        });
      }
    });
    customFieldGroupData && setCustomFieldGroupData(customFieldGroupData);
  }, [identity?.data, tagList]);

  //calculate DOB and age
  const dateOfBirth = identity?.dateOfBirth.split('T')[0] ?? '';
  let age;
  if (dateOfBirth) {
    const birthDate = new Date(dateOfBirth);
    const currentDate = new Date();
    age = Math.max(currentDate.getFullYear() - birthDate.getFullYear(), 0);
  }

  const getGenderValue = () => {
    if (!identity) return '';
    switch (identity.gender.toUpperCase()) {
      case 'M':
        return messages({ id: 'MALE' });
      case 'F':
        return messages({ id: 'FEMALE' });
      case 'N':
        return messages({ id: 'NONBINARY' });
      case 'O':
        return messages({ id: 'NOGENDER' });
      default:
        return identity.gender;
    }
  };

  const personalTableData_personal: ProfileSectionTableData[] = [
    { name: messages({ id: 'MEMBER_PERSONAL_DETAILS_AGE' }), value: age ?? '' },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_DATE_OF_BIRTH' }),
      value: dateOfBirth ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_GENDER' }),
      value: getGenderValue() ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_LANGUAGE' }),
      value: identity?.locale ?? ''
    },
    {
      // todo update value, fetch picture based on photoId
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_PHOTO' }),
      value: (
        <AgTypography
          style={{ cursor: 'pointer' }}
          onClick={() => dispatch(setIsOpenViewAvatar(true))}
        >
          {imageURL ? (
            <img
              src={imageURL}
              alt={messages({ id: 'MEMBER_PERSONAL_DETAILS_PHOTO' })}
              style={{ width: '32px', height: '32px',  verticalAlign: 'middle', objectFit: 'cover'  }}
            />
          ) : (
            <AgAvatar
              src={defaultAvatar}
              alt={messages({ id: 'AVATAR_ALT' })}
              style={{ width: '32px', height: '32px', verticalAlign: 'middle', objectFit: 'cover' }}
            />
          )}
        </AgTypography>
      )
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_NOTES' }),
      value:
        identity?.data?.find((item) => item.key === MEMBER_FIELD_NAME_NOTES)
          ?.value || ''
    }
  ];

  const personalTableData_name: ProfileSectionTableData[] = [
    { name: messages({ id: 'FIRSTNAME' }), value: identity?.firstName ?? '' },
    { name: messages({ id: 'MIDDLENAME' }), value: identity?.middleName ?? '' },
    { name: messages({ id: 'LASTNAME' }), value: identity?.familyName ?? '' }
  ];

  const personalTableData_address: ProfileSectionTableData[] = [
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_COUNTRY' }),
      value: selectedCountry?.name ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_PROVINCE' }),
      value: identity?.address?.province ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_CITY' }),
      value: identity?.address?.city ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_POSTAL_CODE' }),
      value: identity?.address?.postCode ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_1' }),
      value: identity?.address?.addressLine1 ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_2' }),
      value: identity?.address?.addressLine2 ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_3' }),
      value: identity?.address?.addressLine3 ?? ''
    }
  ];

  const personalTableData_GPS: ProfileSectionTableData[] = [
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_LATITUDE' }),
      value: identity?.address?.location?.X ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_LONGITUDE' }),
      value: identity?.address?.location?.Y ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_ALTITUDE' }),
      value: identity?.address?.location?.altitude ?? ''
    }
  ];

  const personalTableData_contact: ProfileSectionTableData[] = [
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_CONTACT_INFO_PHONE' }),
      value: identity?.phoneNumber ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_CONTACT_INFO_EMAIL' }),
      value: identity?.email ?? ''
    }
  ];

  return (
    <AgTypography
      style={{
        padding: '24px 16px',
        display: 'grid',
        gap: '16px'
      }}
    >
      <AgProfileSectionTable
        tableData={personalTableData_name}
        title={messages({ id: 'MEMBER_PERSONAL_DETAILS_NAME' })}
        style={{ maxWidth: '616px' }}
      />
      <Divider sx={{ margin: '16px 0' }} />
      <AgProfileSectionTable
        tableData={personalTableData_personal}
        title={messages({ id: 'MEMBER_PERSONAL_DETAILS_PERSONAL' })}
        style={{ maxWidth: '616px' }}
      />
      <Divider sx={{ margin: '16px 0' }} />
      <AgProfileSectionTable
        tableData={personalTableData_address}
        title={messages({ id: 'ADDRESS' })}
        style={{ maxWidth: '616px' }}
      />
      <Divider sx={{ margin: '16px 0' }} />
      <AgProfileSectionTable
        tableData={personalTableData_GPS}
        title={messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_TITLE' })}
        style={{ maxWidth: '616px' }}
      />
      <AgButton
        label={messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_VIEW_BUTTON' })}
        disabled={!longitude || !latitude}
        variant={'outlined'}
        color={'secondary'}
        onClick={() => {
          if (!longitude || !latitude) return;
          const mapUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
          window.open(mapUrl, '_blank');
        }}
      />
      <Divider sx={{ margin: '16px 0' }} />
      <AgProfileSectionTable
        tableData={personalTableData_contact}
        title={messages({ id: 'MEMBER_PERSONAL_DETAILS_CONTACT_INFO_TITLE' })}
        caption={messages({
          id: 'MEMBER_PERSONAL_DETAILS_CONTACT_INFO_CAPTION'
        })}
        style={{ maxWidth: '616px' }}
      />
      <Divider sx={{ margin: '16px 0' }} />

      {customFieldGroupData?.length ? (
        <AgMultipleCustomField
          data={customFieldGroupData.map((customFieldData) => {
            return {
              title: customFieldData.groupName || '',
              editOnly: true,
              fields: customFieldData.data
            };
          })}
          mainWidth={'auto'}
        />
      ) : null}
    </AgTypography>
  );
};

export default ViewStaffDetails;
